import { getMe } from '@/api/userApi';
import { persistUserTimezone, clearUserTimezone, getUserTimezone } from '@/utils/userTimezone';
import { clearPersistedViewStateStorage } from '@/utils/viewStateStorage';

const toInt = (v) => (v === '' || v == null ? null : Number(v));

let authInitPromise = null;
let authSessionRevision = 0;

const ME_FETCH_MAX_ATTEMPTS = 2;
const ME_FETCH_RETRY_MS = 500;

function resetAuthInitPromise() {
  authInitPromise = null;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isMeFetchRetriable(error) {
  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    return false;
  }
  return !status || status >= 500 || error?.code === 'ERR_NETWORK';
}

export default {
  namespaced: true,
  state: {
    user: {
      isLogged: false,
      token: null,
      name: null,
      email: null,
      status: null,
      id: null,
      departmentId: null,
      timezone: null,
      avatarUrl: null,
      roles: [],
      permissions: []
    },
    isValidating: false,
    flashMessage: null,
    authReady: false
  },
  getters: {
    authReady: (state) => state.authReady,
    isUserEmail: (state) => state.user.email,
    isAuthenticated: (state) => !!state.user.isLogged && !!state.user.token
  },
  mutations: {
    loginUser(state, payload) {
      authSessionRevision += 1;
      const token = payload.jwt || null;
      state.user.isLogged = !!token;
      state.user.token = token;
      state.user.name = payload.name ?? null;
      state.user.email = payload.email ?? null;
      state.user.status = payload.status ?? null;
      state.user.id = toInt(payload.id);
      state.user.departmentId = toInt(payload.department_id);
      if (payload.timezone) {
        state.user.timezone = persistUserTimezone(payload.timezone);
      }
      state.user.roles = Array.isArray(payload.roles) ? payload.roles : [];
      state.user.permissions = Array.isArray(payload.permissions) ? payload.permissions : [];

      try {
        localStorage.setItem('srToken', payload.jwt || '');
        localStorage.setItem('srEmail', payload.email || '');
        localStorage.setItem('srName', payload.name || '');
        localStorage.setItem('srStatus', String(payload.status ?? ''));
        localStorage.setItem('srId', String(payload.id ?? ''));
        localStorage.setItem('srDepartmentId', String(payload.department_id ?? ''));
        localStorage.setItem('srRoles', JSON.stringify(state.user.roles || []));
        localStorage.setItem('srPermissions', JSON.stringify(state.user.permissions || []));
        localStorage.setItem('srIsLogged', 'true');
      } catch (e) { /* ignore */ }
    },
    validateUser(state, payload) {
      authSessionRevision += 1;
      const token = payload.jwt || localStorage.getItem('srToken') || null;
      state.user.isLogged = !!token;
      state.user.token = token;
      state.user.name = payload.name ?? localStorage.getItem('srName') ?? null;
      state.user.email = payload.email ?? localStorage.getItem('srEmail') ?? null;
      state.user.status = payload.status ?? localStorage.getItem('srStatus') ?? null;
      state.user.id = toInt(payload.id ?? localStorage.getItem('srId'));
      state.user.departmentId = toInt(payload.department_id ?? localStorage.getItem('srDepartmentId'));
      if (payload.department_id !== undefined && payload.department_id !== null) {
        try {
          localStorage.setItem('srDepartmentId', String(payload.department_id));
        } catch (e) { /* ignore */ }
      }
      const rolesFromPayload = Array.isArray(payload.roles) ? payload.roles : null;
      state.user.roles = rolesFromPayload ?? (() => {
        try {
          const stored = localStorage.getItem('srRoles');
          return stored ? JSON.parse(stored) : [];
        } catch (e) {
          return [];
        }
      })();
      const permsFromPayload = Array.isArray(payload.permissions) ? payload.permissions : null;
      state.user.permissions = permsFromPayload ?? (() => {
        try {
          const stored = localStorage.getItem('srPermissions');
          return stored ? JSON.parse(stored) : [];
        } catch (e) {
          return [];
        }
      })();
      state.user.timezone = getUserTimezone();
    },
    setMeData(state, payload) {
      if (payload.id !== undefined) state.user.id = toInt(payload.id);
      if (payload.name !== undefined) state.user.name = payload.name ?? null;
      if (payload.email !== undefined) state.user.email = payload.email ?? null;
      if (payload.status !== undefined) state.user.status = payload.status ?? null;
      if (Array.isArray(payload.roles)) state.user.roles = payload.roles;
      if (Array.isArray(payload.permissions)) state.user.permissions = payload.permissions;
      if (payload.department_id !== undefined) {
        state.user.departmentId = toInt(payload.department_id);
      }
      if (payload.timezone !== undefined) {
        state.user.timezone = persistUserTimezone(payload.timezone);
      }
      if (payload.avatar_url !== undefined) {
        const url = String(payload.avatar_url ?? '').trim();
        state.user.avatarUrl = url || null;
      }
      try {
        if (payload.id !== undefined) {
          localStorage.setItem('srId', String(payload.id ?? ''));
        }
        if (payload.name !== undefined) {
          localStorage.setItem('srName', payload.name ?? '');
        }
        if (payload.email !== undefined) {
          localStorage.setItem('srEmail', payload.email ?? '');
        }
        if (payload.status !== undefined) {
          localStorage.setItem('srStatus', String(payload.status ?? ''));
        }
        if (payload.department_id !== undefined) {
          localStorage.setItem('srDepartmentId', String(payload.department_id ?? ''));
        }
        localStorage.setItem('srRoles', JSON.stringify(state.user.roles || []));
        localStorage.setItem('srPermissions', JSON.stringify(state.user.permissions || []));
      } catch (e) { /* ignore */ }
    },
    setValidating(state, isValidating) {
      state.isValidating = !!isValidating;
    },
    logoutUser(state) {
      authSessionRevision += 1;
      resetAuthInitPromise();
      state.user.isLogged = false;
      state.user.token = null;
      state.user.name = null;
      state.user.email = null;
      state.user.status = null;
      state.user.id = null;
      state.user.departmentId = null;
      state.user.timezone = null;
      state.user.avatarUrl = null;
      state.user.roles = [];
      state.user.permissions = [];
      state.authReady = true;
      clearUserTimezone();
      clearPersistedViewStateStorage();
      try {
        localStorage.removeItem('srToken');
        localStorage.removeItem('srEmail');
        localStorage.removeItem('srName');
        localStorage.removeItem('srStatus');
        localStorage.removeItem('srId');
        localStorage.removeItem('srDepartmentId');
        localStorage.removeItem('srRoles');
        localStorage.removeItem('srPermissions');
        localStorage.removeItem('srIsLogged');
      } catch (e) { /* ignore */ }
    },
    clearUserData(state) {
      authSessionRevision += 1;
      resetAuthInitPromise();
      state.user.isLogged = false;
      state.user.token = null;
      state.user.name = null;
      state.user.email = null;
      state.user.status = null;
      state.user.id = null;
      state.user.departmentId = null;
      state.user.timezone = null;
      state.user.avatarUrl = null;
      state.user.roles = [];
      state.user.permissions = [];
      state.authReady = true;
      clearUserTimezone();
    },
    setFlashMessage(state, message) {
      state.flashMessage = message;
    },
    clearFlashMessage(state) {
      state.flashMessage = null;
    },
    setAuthReady(state, value) {
      state.authReady = !!value;
    }
  },
  actions: {
    loginUser({ commit }, payload) {
      resetAuthInitPromise();
      commit('loginUser', payload);
      commit('setAuthReady', true);
    },
    async fetchMe({ commit, state }) {
      const token = state.user?.token || localStorage.getItem('srToken');
      if (!token) return false;
      const revision = authSessionRevision;
      const isCurrentSession = () => revision === authSessionRevision
        && state.user?.token === token
        && localStorage.getItem('srToken') === token;

      for (let attempt = 1; attempt <= ME_FETCH_MAX_ATTEMPTS; attempt++) {
        if (!isCurrentSession()) return false;
        try {
          const data = await getMe(token);
          if (!isCurrentSession()) return false;
          commit('setMeData', {
            id: data.id,
            name: data.name,
            email: data.email,
            status: data.status,
            roles: data.roles || [],
            permissions: data.permissions || [],
            department_id: data.department_id,
            timezone: data.timezone,
            avatar_url: data.avatar_url
          });
          return true;
        } catch (e) {
          if (!isCurrentSession()) return false;
          if (e?.response?.status === 401 || e?.response?.status === 403) {
            commit('logoutUser');
            return false;
          }
          if (attempt < ME_FETCH_MAX_ATTEMPTS && isMeFetchRetriable(e)) {
            await sleep(ME_FETCH_RETRY_MS);
            continue;
          }
          commit('logoutUser');
          return false;
        }
      }

      if (isCurrentSession()) commit('logoutUser');
      return false;
    },
    async initializeAuth({ commit, state, dispatch }) {
      if (state.authReady) return;
      if (authInitPromise) return authInitPromise;

      authInitPromise = (async () => {
        try {
          commit('setValidating', true);

          const token = localStorage.getItem('srToken');
          const isLogged = localStorage.getItem('srIsLogged');

          if (!token || isLogged !== 'true') {
            commit('clearUserData');
            commit('setAuthReady', true);
            return;
          }

          const payload = { jwt: token };
          commit('validateUser', payload);

          const meOk = await dispatch('fetchMe');
          if (!meOk) {
            commit('setAuthReady', true);
            return;
          }

          commit('setAuthReady', true);
        } catch (e) {
          commit('clearUserData');
          commit('setAuthReady', true);
        } finally {
          commit('setValidating', false);
        }
      })();

      return authInitPromise;
    }
  },
  modules: {}
};
